import { Reveal, Overline } from "./Reveal";
import { Phone, Mail, ArrowUpRight, Star } from "lucide-react";
import { COPOU_AGENT, TEAM, SITES } from "../data/content";

export const Team = ({ t, lang }) => (
  <section id="team" data-testid="team-section" className="py-24 md:py-36 bg-copou-surface">
    <div className="max-w-7xl mx-auto px-6 md:px-12">
      {/* Featured Copou agent */}
      <Reveal className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center mb-24">
        <div className="relative order-2 lg:order-1">
          <Overline>{t.team.overline}</Overline>
          <h2 className="font-serif font-light text-4xl sm:text-5xl tracking-tight mt-4 text-copou-ink">{t.team.title}</h2>
          <p className="mt-6 text-copou-muted leading-relaxed text-base md:text-lg">{t.team.agentText}</p>

          <div className="mt-8 bg-copou-bg border border-copou-line p-7">
            <div className="flex items-center gap-2 text-copou-primary mb-2">
              <Star size={14} fill="currentColor" />
              <span className="text-xs font-semibold uppercase tracking-wider">{COPOU_AGENT.role[lang]}</span>
            </div>
            <h3 className="font-serif text-3xl text-copou-ink">{COPOU_AGENT.name}</h3>
            <p className="text-sm text-copou-muted mt-2">{COPOU_AGENT.zones[lang]}</p>
            <div className="mt-6 flex flex-col sm:flex-row gap-3">
              <a
                href={`tel:${COPOU_AGENT.phoneTel}`}
                data-testid="agent-call-btn"
                className="inline-flex items-center justify-center gap-2 bg-copou-primary text-white px-6 py-3.5 text-sm font-semibold uppercase tracking-wider hover:bg-copou-primaryHover transition-colors"
              >
                <Phone size={16} /> {COPOU_AGENT.phone}
              </a>
              <a
                href={COPOU_AGENT.url}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="agent-profile-btn"
                className="inline-flex items-center justify-center gap-2 border border-copou-primary text-copou-primary px-6 py-3.5 text-sm font-semibold uppercase tracking-wider hover:bg-copou-primary hover:text-white transition-colors"
              >
                {t.team.profileBtn} <ArrowUpRight size={16} />
              </a>
            </div>
            <a href={`mailto:${COPOU_AGENT.email}`} className="mt-4 inline-flex items-center gap-2 text-sm text-copou-muted hover:text-copou-primary transition-colors">
              <Mail size={15} /> {COPOU_AGENT.email}
            </a>
          </div>
        </div>

        <div className="order-1 lg:order-2 relative">
          <div className="absolute -inset-3 border border-copou-primary/30 -z-0" />
          <img src={COPOU_AGENT.img} alt={COPOU_AGENT.name} className="relative w-full h-[420px] md:h-[520px] object-cover object-top" />
        </div>
      </Reveal>

      {/* Full team grid */}
      <Reveal className="mb-12">
        <h3 className="font-serif text-3xl md:text-4xl font-light text-copou-ink">{t.team.teamTitle}</h3>
        <p className="mt-3 text-copou-muted max-w-xl">{t.team.teamSubtitle}</p>
      </Reveal>

      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-6">
        {TEAM.map((m, i) => (
          <Reveal key={i} delay={(i % 4) * 0.06}>
            <div data-testid={`team-member-${i}`} className="group bg-copou-bg border border-copou-line p-5 h-full hover:shadow-lg transition-all duration-300">
              <a href={m.url} target="_blank" rel="noopener noreferrer" data-testid={`team-member-link-${i}`} className="block">
                <div className="aspect-square overflow-hidden mb-4">
                  <img src={m.img} alt={m.name} className="w-full h-full object-cover object-top grayscale group-hover:grayscale-0 transition-all duration-500" />
                </div>
                <h4 className="font-serif text-lg text-copou-ink leading-tight group-hover:text-copou-primary transition-colors">{m.name}</h4>
              </a>
              <p className="text-xs text-copou-muted mt-1 min-h-[2rem]">{m.role[lang]}</p>
              <a href={`tel:+4${m.phone.replace(/\s/g, "")}`} className="mt-3 inline-flex items-center gap-1.5 text-sm font-medium text-copou-primary hover:gap-2.5 transition-all">
                <Phone size={13} /> {m.phone}
              </a>
              <a href={m.url} target="_blank" rel="noopener noreferrer" className="mt-2 flex items-center gap-1 text-xs uppercase tracking-wider text-copou-muted hover:text-copou-primary transition-colors">
                {t.team.profileBtn} <ArrowUpRight size={13} />
              </a>
            </div>
          </Reveal>
        ))}
      </div>

      <Reveal delay={0.1} className="mt-12 text-center">
        <a
          href={SITES.team}
          target="_blank"
          rel="noopener noreferrer"
          data-testid="team-all-cta"
          className="inline-flex items-center gap-2 border border-copou-primary text-copou-primary px-8 py-4 text-sm font-semibold uppercase tracking-wider hover:bg-copou-primary hover:text-white transition-colors"
        >
          {t.team.allTeam} <ArrowUpRight size={16} />
        </a>
      </Reveal>
    </div>
  </section>
);
